import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Save, ArrowLeft, Plus, X, Image as ImageIcon } from 'lucide-react';

const UpdateCategory = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [features, setFeatures] = useState([]);
    const [currentImage, setCurrentImage] = useState(''); // Old Image URL
    const [newImage, setNewImage] = useState(null); // New File Object
    const [preview, setPreview] = useState('');

    useEffect(() => {
        const fetchDetails = async () => { 
            try {
                const token = localStorage.getItem('adminToken');
                console.log(id);
                const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/adminworkspace001marvelsato/marvel-sato-management/viewonecategory/${id}`, { 
                    headers: { authorization: `Bearer ${token}` }
                });
                const cat = res.data.category;
                setName(cat.categoryName); 
                setDescription(cat.categoryDescription);
                setFeatures(cat.features || []);
                setCurrentImage(cat.imageId);
            } catch (err) {
                console.error(err);
                alert("Failed to fetch details");
            } finally {
                setLoading(false);
            }
        };
        fetchDetails();
    }, [id]);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        setNewImage(file);
        setPreview(URL.createObjectURL(file)); // Create local preview
    };

    const handleFeatureChange = (index, e) => {
        const { name, value } = e.target;
        const list = [...features];
        list[index][name] = value;
        setFeatures(list);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setUpdating(true);
        const token = localStorage.getItem('adminToken');
        const formData = new FormData();
        formData.append('categoryName', name);
        formData.append('categoryDescription', description);
        if (newImage) formData.append('image', newImage);
        formData.append('features', JSON.stringify(features));

        try {
            await axios.put(`${process.env.REACT_APP_API_URL}/api/adminworkspace001marvelsato/marvel-sato-management/updatecategory/${id}`, formData, {
                headers: { 
                    authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data' 
                }
            });
            alert("Updated Successfully!");
            navigate('/admin/manage-categories');
        } catch (err) {
            console.error(err);
            alert("Update failed");
        } finally {
            setUpdating(false);
        }
    };

    if (loading) return <div className="h-screen flex items-center justify-center"><Loader2 className="animate-spin text-orange-500" /></div>;

    return (
        <div className="p-8 max-w-4xl mx-auto">
            <button onClick={() => navigate(-1)} className="mb-6 flex items-center gap-2 text-slate-500 font-bold uppercase text-[10px] hover:text-orange-500 transition-all">
                <ArrowLeft size={14} /> Back to Catalog
            </button>

            <h1 className="text-3xl font-black uppercase italic mb-8">Edit <span className="text-orange-500">Category</span></h1>

            <form onSubmit={handleSubmit} className="space-y-8 bg-white p-8 rounded-3xl border border-slate-100 shadow-sm">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* LEFT: INFO */}
                    <div className="space-y-6">
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase text-slate-400">Category Name</label>
                            <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full p-4 bg-slate-50 rounded-2xl font-bold" />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase text-slate-400">Description</label>
                            <textarea value={description} onChange={(e) => setDescription(e.target.value)} className="w-full p-4 bg-slate-50 rounded-2xl h-32 text-sm" />
                        </div>
                    </div>

                    {/* RIGHT: IMAGE PREVIEW */}
                    <div className="space-y-4">
                        <label className="text-[10px] font-black uppercase text-slate-400">Category Visual</label>
                        <div className="relative aspect-video rounded-2xl overflow-hidden bg-slate-100 border-2 border-dashed border-slate-200 flex items-center justify-center">
                            <img src={preview || currentImage} className="w-full h-full object-cover" alt="preview" />
                            <label className="absolute bottom-2 right-2 bg-white px-4 py-2 rounded-xl shadow-lg cursor-pointer text-[10px] font-black uppercase flex items-center gap-2 hover:bg-orange-500 hover:text-white transition-all">
                                <ImageIcon size={14} /> Change Image
                                <input type="file" className="hidden" onChange={handleImageChange} />
                            </label>
                        </div>
                    </div>
                </div>

                {/* FEATURES */}
                <div className="bg-slate-50 p-6 rounded-3xl space-y-4">
                    <div className="flex justify-between items-center">
                        <h3 className="text-[10px] font-black uppercase text-slate-400">Technical Specs</h3>
                        <button type="button" onClick={() => setFeatures([...features, { specType: '', specValue: '' }])} className="p-2 bg-slate-900 text-white rounded-full"><Plus size={16}/></button>
                    </div>
                    {features.map((f, i) => (
                        <div key={i} className="flex gap-3">
                            <input name="specType" value={f.specType} onChange={(e) => handleFeatureChange(i, e)} className="flex-1 p-3 rounded-xl text-xs" placeholder="Spec Type" />
                            <input name="specValue" value={f.specValue} onChange={(e) => handleFeatureChange(i, e)} className="flex-1 p-3 rounded-xl text-xs" placeholder="Value" />
                            <button type="button" onClick={() => setFeatures(features.filter((_, idx) => idx !== i))} className="text-red-500"><X size={18}/></button>
                        </div>
                    ))}
                </div>

                <button type="submit" disabled={updating} className="w-full py-4 bg-orange-500 text-white font-black uppercase tracking-widest rounded-2xl hover:bg-slate-900 transition-all flex items-center justify-center gap-2 disabled:opacity-50 shadow-xl shadow-orange-500/20">
                    {updating ? <Loader2 className="animate-spin" /> : <><Save size={18} /> Update Category</>}
                </button>
            </form>
        </div>
    );
};

export default UpdateCategory; 